/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Stack, Grid2, Box, IconButton, Tooltip} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import style from './AppointmentTable.module.css';
import { Appointment, AppointmentData, AppointmentStatus, ColumnConfig, DepartmentDetail, GroupConfig } from '../../types';
import EditableTable from '../customTableComponent';
import { useLoader } from '../loaderProvider/loader';
import { useAlert } from '../alertProvider';
import { getAppointmentsList} from '../../services/apiService';
import AppointmentModal from './../appointmentForm/appointmentForm'; // Make sure to import the modal component
import { useClinic } from '../ClinicProvider';
import ActionsMenu from './actionMenu';
import { convertUTCToDesiredTimezone } from '../../services/time';
import ReusableCustombtn from '../ResuableCustombtnComponent';
import { ReactComponent as PrinterIcon } from '../../assets/SVG/printer (1).svg';
import ReusableReportComponent from '../printReports/ReusableReportComponent';
import { getTodayDefaultDate, getOneMonthAgoDate } from '../../utils/commonUtils';
import AppointmentReport from './AppointmentReport';




interface AppointmentTableProps {
  departmentID?: number | undefined;
  departmentName?: string | '';
  departmentTabs?:DepartmentDetail[];
  handleTabChange?: Dispatch<SetStateAction<DepartmentDetail | undefined>>;
}

export default function AppointmentTable({ departmentID , departmentName, departmentTabs, handleTabChange }: AppointmentTableProps) {
  const { t: tCalendar } = useTranslation('calendar');
  const { t: tGeneral } = useTranslation('general');
  const { t: tAppointmentForm } = useTranslation('appointment_form');
  const { t: tRegistration } = useTranslation('registration_lang');

  const groupConfig: GroupConfig[] = [
    { fields: ['date'], type: 'row' },
    { fields: ['TimeFrom'], type: 'row' },
    { fields: ['TimeTo'], type: 'row' },
    { fields: ['patientName', 'MRN'], type: 'column', defaultHeaderGroupname: tCalendar('patientName') },
    { fields: ['mobileNumber'], type: 'row' },
    { fields: ['department'], type: 'row' },
    { fields: ['doctor'], type: 'row' },
    { fields: ['Reason'], type: 'row' },
    { fields: ['action'], type: 'row' },
  ];

  const columns: ColumnConfig[] = [
    { field: 'date', headerName: tAppointmentForm('dateLabel'), type: 'date', editable: false },
    { field: 'TimeFrom', headerName: tAppointmentForm('timeFromLabel'), type: 'text', editable: false },
    { field: 'TimeTo', headerName: tAppointmentForm('timeToLabel'), type: 'text', editable: false },
    { field: 'patientName', headerName: 'Patient Name', type: 'text', editable: false },
    { field: 'MRN', headerName: tRegistration('Registration.MRN'), type: 'text', editable: false },
    { field: 'mobileNumber', headerName: tRegistration('Registration.Text_Phone_Number'), type: 'text', editable: false },
    { field: 'doctor', headerName: tRegistration('Registration.Doctor'), type: 'text', editable: false },
    { field: 'department', headerName: tAppointmentForm('departmentLabel'), type: 'text', editable: false },
    { field: 'Reason', headerName: tAppointmentForm('appointmentReasonLabel'), type: 'text', editable: false },
    { field: 'action', headerName: tCalendar('action'), type: 'text', editable: false },
  ];

  const { showLoader, hideLoader } = useLoader();
  const { addAlert } = useAlert();
  const { ClinicId } = useClinic();
  const [appointmentData, setAppointmentData] = useState<AppointmentData[]>([]);
  const [dataSize, setDataSize] = useState<number | undefined>();
  const [paginationMeta, setPaginationMeta] = useState<{
    limit: number;
    offset: number;
    total_count: number;
    next?: string;
    previous?: string;
  } | undefined>();
  const [modalOpen, setModalOpen] = useState(false); // State for modal open/close
  const [doctor, setDoctor] = useState(''); // State for patient name
  const [appointments, setAppointments] = useState<Appointment[] | undefined>();
  const [scheduled, setScheduled] = useState(0);
  const [checkedIn, setCheckedIn] = useState(0);
  const [checkedOut, setCheckedOut] = useState(0);
  const [unAttended, setUnAttended] = useState(0);
  const [cancelled, setCancelled] = useState(0);
  const [filterAppointments, setFilterAppointments ] = useState<string | undefined>(undefined);
  const [currentPageoffset, setCurrentPageoffset] = useState(1);
  const [filteredAppointmentData, setFilteredAppointmentData] = useState<AppointmentData[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [startDate, setStartDate] = useState(getOneMonthAgoDate()); 
  const [endDate, setEndDate] = useState(getTodayDefaultDate());  
  const [showPrint, setShowPrint] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
 
  const handleOnChange = (id: number, field: string, value: string) => {

  };

  const handleAction = (id: number) => {

  };

  const handlePageChange = (newPage: number) => {
    setCurrentPageoffset(newPage);
    // Keep the current filter when changing pages - don't reset it
  };

  const formatTimeTo12Hour = (time: string | undefined) => {
    if (!time || typeof time !== 'string' || !time.includes(':')) {
      console.error('Invalid time format:', time);
      return '';
    }

    const date = new Date(time);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    return `${formattedHours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };

  const defaultAppointmentDate = new Date();

  const calculateStatusCounts = (appointments: Appointment[]) => {
    return appointments.reduce(
      (counts, appointment) => {
        switch (appointment.status) {
        case AppointmentStatus.CANCELLED:
          counts.cancelled += 1;
          break;
        case AppointmentStatus.CHECKED_IN:
          counts.checkedIn += 1;
          break;
        case AppointmentStatus.CHECKED_OUT:
          counts.checkedOut += 1;
          break;
        case AppointmentStatus.SCHEDULED:
          counts.scheduled += 1;
          break;
        case AppointmentStatus.UNATTENDED:
          counts.unAttended += 1;
          break;
        }
        return counts;
      },
      { cancelled: 0, checkedIn: 0, checkedOut: 0, scheduled: 0, unAttended: 0 }
    );
  };

  const getOffset = useCallback(() : number =>{
    let offset;
    const itemsPerPagePageoffset = 10;
    if (currentPageoffset === 1) {
      offset = (itemsPerPagePageoffset * (currentPageoffset - 1));
    } else {
      offset = (itemsPerPagePageoffset * (currentPageoffset - 1)) + 1;
    }
    return offset;
  },[ currentPageoffset ]);

  useEffect(() => {
    if (appointments && filterAppointments === undefined ) {
      const statusCounts = calculateStatusCounts(appointments);
      setCancelled(statusCounts.cancelled);
      setCheckedIn(statusCounts.checkedIn);
      setCheckedOut(statusCounts.checkedOut);
      setScheduled(statusCounts.scheduled);
      setUnAttended(statusCounts.unAttended);
    }
  }, [appointments, filterAppointments]);

  useEffect(()=>{
    setFilterAppointments( undefined );
    setCurrentPageoffset(1); 
    setAppointmentData([]);
  },[departmentID]);
  
  useEffect(() => {
    // Clear any existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    showLoader();
  
    // Debounced fetch appointments
    debounceTimeoutRef.current = setTimeout(() => {
      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Fetch appointments
      const fetchAppointments = () => {
        // Make sure to handle the case when departmentID is not defined
        if (departmentID) {
          getAppointmentsList({ 
            tenantId: ClinicId, 
            departmentID: departmentID, 
            status: filterAppointments, 
            offset: getOffset(), 
            limit: 10, 
            search: inputValue ?? undefined, 
            afterDate: startDate, 
            beforeDate: endDate,
            signal 
          })
            .then((appointmentDataResponse) => {
              // Handle different response structures - objects or data
              const appointmentsArray = appointmentDataResponse.objects ?? appointmentDataResponse.data ?? [];
              const totalCount = appointmentDataResponse.meta?.total_count ?? appointmentsArray.length;
              const meta = appointmentDataResponse.meta ?? { total_count: totalCount, matched_count: appointmentsArray.length };
              
              setDataSize(totalCount);
              setPaginationMeta(meta);
              setAppointments(appointmentsArray);
              const transformedAppointments: AppointmentData[] = appointmentsArray.map((appointment: Appointment) => {
                const appointmentDateStr = appointment.appointment_date;
                const createdDate = appointmentDateStr ? new Date(appointmentDateStr) : null;
                const isValidDate = createdDate && !Number.isNaN(createdDate.getTime());
                const formattedDate = isValidDate
                  ? `${createdDate.getUTCDate().toString().padStart(2, '0')}/${(createdDate.getUTCMonth() + 1).toString().padStart(2, '0')}/${createdDate.getUTCFullYear()}`
                  : '-';
                return {
                  id: appointment.id,
                  gender: appointment.patient?.gender || '-',
                  age: appointment.patient?.age || '-',
                  reasonOfVisit: appointment.patient?.additional_info?.reason_for_visit || '-',
                  date: formattedDate,
                  TimeFrom: convertUTCToDesiredTimezone(appointment.start_datetime),
                  TimeTo: convertUTCToDesiredTimezone(appointment.end_datetime),
                  patientName: `${appointment.patient?.first_name || ''} ${appointment.patient?.last_name || ''}`.trim() || '-',
                  MRN: `${appointment.patient?.medical_record_no || '-'} | ${appointment.patient?.gender || '-'}`,
                  mobileNumber: appointment.patient?.mobile_number || '-',
                  doctor: `${appointment?.user?.first_name ?? ''} ${appointment?.user?.last_name ?? ''}`.trim() || '-',
                  firstvisit: appointment.department__name || '-',
                  action: (<ActionsMenu appointment={appointment} />),
                  appointmentDate: appointment.appointment_date || '-',
                  appointmentReason: appointment.appointment_reason__reason__label || '-',
                  department: appointment.department__name || '-',
                  Reason: appointment.appointment_reason__reason__label || '-', // Map the reason for visit correctly here
                  startTime: appointment.start_datetime || '-',
                  endTime: appointment.end_datetime || '-',
                  patient: `${appointment.patient?.first_name || ''} ${appointment.patient?.last_name || ''}`.trim() || '-',
                  status: appointment.status || '',
                };
              });
    
              setAppointmentData(transformedAppointments);
            })
            .catch((error) => {
              // Don't show error if request was aborted
              if (error.name === 'CanceledError' || error.message === 'canceled') {
                return;
              }
              console.error('Error fetching appointments:', error);
              addAlert('Error fetching appointments. Please try again later.', 'error');
            })
            .finally(() => {
              hideLoader();
            });
        } else {
          hideLoader();
        }
      };
    
      fetchAppointments();
    }, 300); // 300ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [addAlert, showLoader, hideLoader, ClinicId, departmentID, filterAppointments, currentPageoffset, filteredAppointmentData, getOffset, inputValue, startDate, endDate]);
  

  const handleOpenModal = () => {
    setModalOpen(true); // Open the modal
    setDoctor(''); // Optionally reset patient name or set it to a specific value
  };

  const handleCloseModal = () => {
    setModalOpen(false); // Close the modal
  };

  const handleSetFilterAppointments = (status: string | undefined) => {
    setCurrentPageoffset(1); // Reset to first page when filter changes
    setFilteredAppointmentData([]); // Clear local filter as we'll fetch from API
    setFilterAppointments(status);
  };
  
  const handleDateChange = (start: string, end: string) => {
    if(start > end) {      
      return;
    }
    setStartDate(start);
    setEndDate(end);
  };

  const timeOut = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = (value: string) => {
    if (timeOut.current) {
      clearTimeout(timeOut.current);
    }

    timeOut.current = setTimeout(() => {
      setInputValue(value);
    }, 800);
  };

  const handleClose = () => {
    setShowPrint(false);
  };

  const handlePrintReport = async () => {
    showLoader();
    try {
      const response = await getAppointmentsList({
        tenantId: ClinicId,
        departmentID: departmentID,
        status: filterAppointments,
        limit: paginationMeta?.total_count || 1000,
        search: inputValue ?? undefined,
        afterDate: startDate,
        beforeDate: endDate,
      });
      
      // Handle different response structures - objects or data
      const appointmentsArray = response.objects ?? response.data ?? [];
      
      const allAppointments: AppointmentData[] = appointmentsArray.map((appointment: Appointment) => {
        const appointmentDateStr = appointment.appointment_date;
        const createdDate = appointmentDateStr ? new Date(appointmentDateStr) : null;
        const isValidDate = createdDate && !Number.isNaN(createdDate.getTime());
        const formattedDate = isValidDate
          ? `${createdDate.getUTCDate().toString().padStart(2, '0')}/${(createdDate.getUTCMonth() + 1).toString().padStart(2, '0')}/${createdDate.getUTCFullYear()}`
          : '-';
        return {
          id: appointment.id,
          gender: appointment.patient?.gender || '-',
          age: appointment.patient?.age || '-',
          reasonOfVisit: appointment.patient?.additional_info?.reason_for_visit || '-',
          date: formattedDate,
          TimeFrom: convertUTCToDesiredTimezone(appointment.start_datetime),
          TimeTo: convertUTCToDesiredTimezone(appointment.end_datetime),
          patientName: `${appointment.patient?.first_name || ''} ${appointment.patient?.last_name || ''}`.trim() || '-',
          MRN: `${appointment.patient?.medical_record_no || '-'} | ${appointment.patient?.gender || '-'}`,
          mobileNumber: appointment.patient?.mobile_number || '-',
          doctor: `${appointment?.user?.first_name ?? ''} ${appointment?.user?.last_name ?? ''}`.trim() || '-',
          firstvisit: appointment.department__name || '-',
          action: (<ActionsMenu appointment={appointment} />),
          appointmentDate: appointment.appointment_date || '-',
          appointmentReason: appointment.appointment_reason__reason__label || '-',
          department: appointment.department__name || '-',
          Reason: appointment.appointment_reason__reason__label || '-',
          startTime: appointment.start_datetime || '-',
          endTime: appointment.end_datetime || '-',
          patient: `${appointment.patient?.first_name || ''} ${appointment.patient?.last_name || ''}`.trim() || '-',
          status: appointment.status || '',
        };
      });
      
      setFilteredAppointmentData(allAppointments);
      setShowPrint(true);
    } catch (error) {
      console.error('Error fetching all appointments for report:', error);
      addAlert('Error generating report. Please try again later.', 'error');
    } finally {
      hideLoader();
    }
  };

  return (
    <div className={style.container}>
      <Grid2 container spacing={3} className={style.container} alignItems='center' sx={{ mt: 2, px: 2 }}>
        <Grid2 size={{xs: 4}} sx={{ display: 'flex',alignItems: 'center', justifyContent: 'start' }}>
          <h2 className={style.title}>{tRegistration('Registration.Patient List')}</h2>
        </Grid2>
        <Grid2 size={{xs: 8}} sx={{ textAlign: 'right' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <ReusableCustombtn
              type={['input', 'date']}
              onDateChange={(start: string, end: string) => handleDateChange(start, end)}
              inputLabel={tRegistration('Registration.Search Patient')}
              inputPlaceholder={tRegistration('Registration.Search by name, MRN, Mobile')}
              dateFromLabel={tRegistration('Registration.Date From')}
              dateToLabel={tRegistration('Registration.Date To')}
              onInputChange={handleInputChange}
              noMaxDate
              resetEnabled
            />
            <Button className={style.button} variant="contained" onClick={handleOpenModal} sx={{ml:2}}> {/* Open modal on click */}
              {tAppointmentForm('newAppointmentButton')}
            </Button>
          </Box>
          {appointmentData.length > 0 && (
            <Tooltip title={tGeneral('print_Print')} arrow>
              <IconButton onClick={handlePrintReport} sx={{ marginTop: '18px' }}>
                <PrinterIcon />
              </IconButton>
            </Tooltip>
          )}
        </Grid2>
      </Grid2>
      <div className={style.status}>
        <Stack direction="row" spacing={1}>
          <Tooltip title={tCalendar('filterByScheduled')} arrow>
            <Chip onClick={() => handleSetFilterAppointments('SCHEDULED')} label={`${tCalendar('scheduled')} ${scheduled}`} variant={filterAppointments === 'SCHEDULED' ? 'filled' : 'outlined'} color='primary' sx={{ opacity: 0.8 }} disabled={scheduled === 0}/>
          </Tooltip>
          <Tooltip title={tCalendar('filterByCheckedIn')} arrow>
            <Chip onClick={() => handleSetFilterAppointments('CHECKED_IN') } label={`${tCalendar('checkedIn')} ${checkedIn}`} variant={filterAppointments === 'CHECKED_IN' ? 'filled' : 'outlined'} color='warning' sx={{ opacity: 0.8 }} disabled={checkedIn === 0}/>
          </Tooltip>
          <Tooltip title={tCalendar('filterByCheckedOut')} arrow>
            <Chip onClick={() => handleSetFilterAppointments('CHECKED_OUT') } label={`${tCalendar('checkedOut')} ${checkedOut}`} variant={filterAppointments === 'CHECKED_OUT' ? 'filled' : 'outlined'} color='warning'sx={{ opacity: 0.8 }} disabled={checkedOut === 0}/>
          </Tooltip>
          <Tooltip title={tCalendar('filterByUnattended')} arrow>
            <Chip onClick={() => handleSetFilterAppointments('UNATTENDED') } label={`${tCalendar('unattended')} ${unAttended}`} variant={filterAppointments === 'UNATTENDED' ? 'filled' : 'outlined'} color='success'sx={{ opacity: 0.8 }} disabled={unAttended === 0}/>
          </Tooltip>
          <Tooltip title={tCalendar('filterByCancelled')} arrow>
            <Chip onClick={() => handleSetFilterAppointments('CANCELLED') } label={`${tCalendar('cancelled')} ${cancelled}`} variant={filterAppointments === 'CANCELLED' ? 'filled' : 'outlined'} color='error'sx={{ opacity: 0.8 }} disabled={cancelled === 0}/>
          </Tooltip>
          { filterAppointments !== undefined  && <Tooltip title={tGeneral('reset')} arrow><IconButton onClick={() => handleSetFilterAppointments(undefined)} ><RestartAltIcon /></IconButton></Tooltip>}
        </Stack>
      </div>
      <div className='mt-4'>
        <EditableTable
          columns={columns}
          data={appointmentData}
          actionType='none'
          onAction={handleAction}
          onChange={handleOnChange}
          dataSize={dataSize}
          groupConfig={groupConfig}
          onPageChange={handlePageChange}
          currentPageoffset={currentPageoffset}
          paginationMeta={paginationMeta}
        />
      </div>
    
      {/* Render the AppointmentModal conditionally */}
      <div>
        <AppointmentModal
          open={modalOpen}
          onClose={handleCloseModal}
          slotDate={defaultAppointmentDate}
          slotDepartment= {departmentID}
          departmentName={departmentName}
          departmentTabs={departmentTabs}
          handleTabChange={handleTabChange}
        />
      </div>
      <ReusableReportComponent
        open={showPrint}
        onClose={handleClose}
        reportTitle={tGeneral('Appointment_Report')}
        showPatientDetails={false}
      >
        <AppointmentReport filteredAppointmentData={filteredAppointmentData} />
      </ReusableReportComponent>

    </div>
  );
}
